import React, { useEffect, useRef } from 'react';
import { useFirebase } from 'providers/firebase/FirebaseContext';

const YELLOW = '#edd714';
const GOLD_GLOW = 'rgba(237, 215, 20, 0.25)';

const styles = {
  root: {
    minHeight: '100vh',
    backgroundColor: '#0d0d0d',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Exo 2, sans-serif',
    padding: '40px 20px',
    position: 'relative',
    overflow: 'hidden',
  },
  bgGlow: {
    position: 'absolute',
    top: '-100px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '500px',
    height: '500px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(237,215,20,0.06) 0%, transparent 70%)',
    pointerEvents: 'none',
  },
  card: {
    backgroundColor: '#141414',
    border: '1px solid #222',
    borderRadius: '24px',
    width: '100%',
    maxWidth: '480px',
    padding: '48px 40px',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
    zIndex: 1,
  },
  topAccent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: `linear-gradient(90deg, transparent, ${YELLOW}, transparent)`,
  },
  iconWrap: {
    width: '96px',
    height: '96px',
    borderRadius: '50%',
    background: 'rgba(237,215,20,0.08)',
    border: '1px solid rgba(237,215,20,0.20)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 28px',
    fontSize: '44px',
  },
  title: {
    fontFamily: 'Orbitron, sans-serif',
    fontSize: '26px',
    fontWeight: 800,
    color: '#ffffff',
    textAlign: 'center',
    letterSpacing: '2px',
    marginBottom: '10px',
  },
  subtitle: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#888',
    textAlign: 'center',
    lineHeight: 1.7,
    marginBottom: '32px',
  },
  dotsRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '32px',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: YELLOW,
    transition: 'opacity 0.3s ease',
  },
  stepsCard: {
    backgroundColor: '#1a1a1a',
    border: '1px solid #2a2a2a',
    borderRadius: '14px',
    padding: '20px',
    marginBottom: '20px',
  },
  stepRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '10px 0',
    position: 'relative',
  },
  stepIconBase: {
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    flexShrink: 0,
    border: '1px solid #333',
    backgroundColor: '#222',
    color: '#555',
  },
  stepIconDone: {
    backgroundColor: YELLOW,
    border: `1px solid ${YELLOW}`,
    color: '#000',
    fontWeight: 800,
  },
  stepIconActive: {
    backgroundColor: 'rgba(237,215,20,0.12)',
    border: '1px solid rgba(237,215,20,0.4)',
    color: YELLOW,
  },
  stepLabel: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#555',
  },
  stepLabelDone: {
    color: '#fff',
    fontWeight: 600,
  },
  stepLabelActive: {
    color: YELLOW,
    fontWeight: 700,
  },
  stepConnector: {
    position: 'absolute',
    left: '16px',
    top: '44px',
    width: '2px',
    height: '20px',
    borderRadius: '1px',
    backgroundColor: '#2a2a2a',
  },
  stepConnectorDone: {
    backgroundColor: YELLOW,
  },
  infoCard: {
    backgroundColor: 'rgba(237,215,20,0.05)',
    border: '1px solid rgba(237,215,20,0.15)',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '28px',
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
  },
  infoIcon: {
    fontSize: '16px',
    flexShrink: 0,
    marginTop: '1px',
  },
  infoText: {
    fontSize: '13px',
    color: '#999',
    lineHeight: 1.6,
  },
  infoHighlight: {
    color: YELLOW,
    fontWeight: 700,
  },
  supportBtn: {
    display: 'block',
    width: '100%',
    padding: '14px',
    background: `linear-gradient(135deg, #b8860b, ${YELLOW})`,
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 800,
    color: '#000',
    letterSpacing: '0.5px',
    cursor: 'pointer',
    marginBottom: '12px',
    boxShadow: `0 4px 18px ${GOLD_GLOW}`,
    fontFamily: 'Exo 2, sans-serif',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  },
  signOutBtn: {
    display: 'block',
    width: '100%',
    padding: '12px',
    backgroundColor: 'transparent',
    border: '1px solid #2a2a2a',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: 600,
    color: '#555',
    cursor: 'pointer',
    fontFamily: 'Exo 2, sans-serif',
    transition: 'color 0.15s ease, border-color 0.15s ease',
  },
};

const STEPS = [
  { icon: '✓', label: 'Application received' },
  { icon: '◉', label: 'Profile under review' },
  { icon: '◎', label: 'Access granted' },
];

export default function PendingApprovalPage({ firstName }) {
  const firebase = useFirebase();
  const dotRefs = [useRef(null), useRef(null), useRef(null)];

  // Animate the three waiting dots sequentially
  useEffect(() => {
    let _frame;
    let tick = 0;
    const interval = setInterval(() => {
      tick++;
      dotRefs.forEach((ref, i) => {
        if (!ref.current) return;
        // Each dot peaks at a different phase
        const phase = (tick + i * 4) % 12;
        ref.current.style.opacity = phase < 4 ? '1' : '0.2';
      });
    }, 200);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@athleteslaunch.com?subject=Account%20Approval%20Request';
  };

  const handleSignOut = () => {
    firebase.logout();
  };

  return (
    <div style={styles.root}>
      <div style={styles.bgGlow} />

      <div style={styles.card}>
        <div style={styles.topAccent} />

        {/* Hourglass icon */}
        <div style={styles.iconWrap}>⏳</div>

        <div style={styles.title}>UNDER REVIEW</div>
        <div style={styles.subtitle}>
          {firstName ? `Hey ${firstName}, your` : 'Your'} application is being reviewed by the Athletes Launch team. You'll get automatic access the moment you're approved.
        </div>

        {/* Animated dots */}
        <div style={styles.dotsRow}>
          {dotRefs.map((ref, i) => (
            <div key={i} ref={ref} style={{ ...styles.dot, opacity: 0.2 }} />
          ))}
        </div>

        {/* Progress steps */}
        <div style={styles.stepsCard}>
          {STEPS.map((step, i) => {
            const done = i === 0;
            const active = i === 1;
            return (
              <div key={i} style={styles.stepRow}>
                {/* Connector line */}
                {i < STEPS.length - 1 && (
                  <div style={{ ...styles.stepConnector, ...(done ? styles.stepConnectorDone : {}) }} />
                )}
                <div style={{
                  ...styles.stepIconBase,
                  ...(done ? styles.stepIconDone : {}),
                  ...(active ? styles.stepIconActive : {}),
                }}>
                  {done ? '✓' : active ? '○' : '○'}
                </div>
                <span style={{
                  ...styles.stepLabel,
                  ...(done ? styles.stepLabelDone : {}),
                  ...(active ? styles.stepLabelActive : {}),
                }}>
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>

        {/* Info */}
        <div style={styles.infoCard}>
          <span style={styles.infoIcon}>ℹ️</span>
          <span style={styles.infoText}>
            We typically review applications within{' '}
            <span style={styles.infoHighlight}>24–48 hours</span>.
            You'll be automatically redirected the moment your account is approved — no need to check back.
          </span>
        </div>

        {/* Actions */}
        <button
          style={styles.supportBtn}
          onClick={handleContactSupport}
          onMouseEnter={e => { e.target.style.transform = 'scale(1.02)'; }}
          onMouseLeave={e => { e.target.style.transform = 'scale(1)'; }}
        >
          ✉ Contact Support
        </button>
        <button
          style={styles.signOutBtn}
          onClick={handleSignOut}
          onMouseEnter={e => { e.target.style.color = '#888'; e.target.style.borderColor = '#444'; }}
          onMouseLeave={e => { e.target.style.color = '#555'; e.target.style.borderColor = '#2a2a2a'; }}
        >
          Sign Out
        </button>
      </div>
    </div>
  );
}
